// ===========================================
// Send login link button (#589)
// ===========================================
// Emails a person a link to set their password. Their current password keeps working until
// they use the link. The caller decides WHETHER to show it (permission + protected-login rule);
// this component owns the confirm step and the pending state. It stays disabled (and the dialog
// open) while the request is in flight: the press waits for the send, and a second press would
// kill the first link (Phase 4 /qa L4).

import { useRef, useState } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { MailOutline } from '@mui/icons-material';
import { ConfirmDialog } from '../common';
import {
  type LoginLinkError,
  useSendParentLoginLink,
  useSendTeacherLoginLink,
} from '../../hooks/useUsers';

/** The server refused because the login is switched off or being deleted (409 `LOGIN_INACTIVE`). */
function isLoginInactiveError(error: unknown): boolean {
  const data = (error as LoginLinkError | null)?.response?.data;
  return !!data && typeof data === 'object' && data.details?.code === 'LOGIN_INACTIVE';
}

export interface SendLoginLinkButtonProps {
  kind: 'parent' | 'teacher';
  /** A Parent id for `parent`, a Teacher id for `teacher`. */
  id: string;
  name: string;
  /** When set the button is disabled and this is shown as the reason. */
  disabledReason?: string;
}

interface ControlProps extends SendLoginLinkButtonProps {
  send: (id: string, options: { onSettled: () => void }) => void;
  pending: boolean;
  error: unknown;
}

const INACTIVE_REASON = 'This login is switched off or being deleted, so a link would not work.';

function LoginLinkControl({ id, name, disabledReason, send, pending, error }: ControlProps) {
  const [open, setOpen] = useState(false);
  // `pending` reaches the button a tick after the click; two clicks inside that tick would send
  // twice, and the second send kills the first link (/qa Phase 5 N1).
  const inFlight = useRef(false);
  // The page cannot see a login that is being deleted; once the server says so, stop offering
  // the press (/qa Phase 5 L3).
  const reason = disabledReason ?? (isLoginInactiveError(error) ? INACTIVE_REASON : undefined);

  const handleConfirm = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    // The hook's snackbar reports success and failure; close the dialog once it settles.
    send(id, {
      onSettled: () => {
        inFlight.current = false;
        setOpen(false);
      },
    });
  };

  return (
    <>
      <Tooltip title={reason ?? ''} disableHoverListener={!reason}>
        <Box component="span" sx={{ display: 'inline-block' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<MailOutline />}
            disabled={!!reason || pending}
            onClick={() => setOpen(true)}
          >
            Send login link
          </Button>
        </Box>
      </Tooltip>
      {reason && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {reason}
        </Typography>
      )}
      <ConfirmDialog
        open={open}
        title="Send login link"
        message={`Email ${name} a link to set their password? Their current password keeps working until they use it.`}
        confirmLabel="Send link"
        loading={pending}
        onConfirm={handleConfirm}
        onCancel={() => setOpen(false)}
      />
    </>
  );
}

// One hook per kind, in its own component, so a page that only ever shows one kind never
// needs the other's hook.
function ParentLoginLink(props: SendLoginLinkButtonProps) {
  const mutation = useSendParentLoginLink();
  return (
    <LoginLinkControl {...props} send={mutation.mutate} pending={mutation.isPending} error={mutation.error} />
  );
}

function TeacherLoginLink(props: SendLoginLinkButtonProps) {
  const mutation = useSendTeacherLoginLink();
  return (
    <LoginLinkControl {...props} send={mutation.mutate} pending={mutation.isPending} error={mutation.error} />
  );
}

// Keyed on the person, so one person's refusal never disables the button for the next.
export default function SendLoginLinkButton(props: SendLoginLinkButtonProps) {
  return props.kind === 'parent' ? (
    <ParentLoginLink key={props.id} {...props} />
  ) : (
    <TeacherLoginLink key={props.id} {...props} />
  );
}
